import { z } from 'zod';
import type { UserRole } from './auth';

// --- Zod Schemas ---

export const moduleEntrySchema = z.object({
  id: z.string().min(1).regex(/^[a-z0-9-]+$/, 'ID must be lowercase alphanumeric with hyphens'),
  name: z.string().min(1).max(100),
  description: z.string().max(500),
  icon: z.string().min(1),
  routePath: z.string().startsWith('/modules/'),
  sourceUrl: z.string().url().optional(),
  requiredRole: z.enum(['admin', 'user', 'read-only']),
  embeddingMethod: z.enum(['iframe', 'new-tab', 'native']),
  fullWidth: z.boolean().optional(),
});

export const moduleRegistrySchema = z.object({
  version: z.string(),
  modules: z.array(moduleEntrySchema),
});

// --- TypeScript Interfaces ---

export interface ModuleEntry {
  id: string;
  name: string;
  description: string;
  icon: string;
  routePath: string;
  sourceUrl?: string;
  requiredRole: UserRole;
  embeddingMethod: 'iframe' | 'new-tab' | 'native';
  fullWidth?: boolean;
}

export interface ModuleRegistryConfig {
  version: string;
  modules: ModuleEntry[];
}

// --- Parser & Printer ---

/**
 * Parses raw config, returns only valid entries.
 * Logs a warning for each invalid entry with its ID and failure reason.
 */
export function parseModuleRegistry(raw: unknown): ModuleEntry[] {
  const result = moduleRegistrySchema.safeParse(raw);

  if (result.success) {
    return result.data.modules as ModuleEntry[];
  }

  // Schema-level failure — try to salvage individual entries
  if (typeof raw !== 'object' || raw === null || !('modules' in raw)) {
    console.warn('[ModuleRegistry] Invalid config: missing or malformed "modules" array');
    return [];
  }

  const rawModules = (raw as Record<string, unknown>).modules;
  if (!Array.isArray(rawModules)) {
    console.warn('[ModuleRegistry] Invalid config: "modules" is not an array');
    return [];
  }

  const valid: ModuleEntry[] = [];

  for (const entry of rawModules) {
    const entryResult = moduleEntrySchema.safeParse(entry);
    if (entryResult.success) {
      valid.push(entryResult.data as ModuleEntry);
    } else {
      const entryId =
        typeof entry === 'object' && entry !== null && 'id' in entry
          ? String((entry as Record<string, unknown>).id)
          : 'unknown';
      const reasons = entryResult.error.issues
        .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
        .join('; ');
      console.warn(`[ModuleRegistry] Invalid entry "${entryId}": ${reasons}`);
    }
  }

  return valid;
}

/**
 * Formats a typed ModuleEntry array back into a ModuleRegistryConfig.
 */
export function printModuleRegistry(entries: ModuleEntry[]): ModuleRegistryConfig {
  return {
    version: '1.0',
    modules: entries.map((entry) => ({ ...entry })),
  };
}
